/**
 * Admin dashboard shell layout.
 *
 * Sidebar navigation driven by workspace capabilities.
 * Responsive: sidebar collapses to hamburger on mobile.
 */

import { useState, useCallback, type ReactNode } from 'react';
import { useWorkspace } from '../hooks/useWorkspace';
import { useAdminAuth } from '../auth/AdminAuthProvider';
import { MatcoraBrandMark } from './MatcoraBrand';

interface AdminShellProps {
  children: ReactNode;
  activePage: string;
  onNavigate: (page: string) => void;
}

interface NavItem {
  id: string;
  label: string;
  capability: string | null;
  icon: ReactNode;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'overview', label: 'Overview', capability: 'overview', icon: <BarChartIcon /> },
  { id: 'performance', label: 'Performance', capability: 'performance', icon: <ActivityIcon /> },
  { id: 'conversations', label: 'Conversations', capability: 'conversations', icon: <MessageIcon /> },
  { id: 'team', label: 'Team Access', capability: 'team_access', icon: <UsersIcon /> },
];

export function AdminShell({ children, activePage, onNavigate }: AdminShellProps) {
  const { workspace, clearWorkspace } = useWorkspace();
  const { user, logout } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  const visibleItems = NAV_ITEMS.filter(
    (item) => !item.capability || workspace?.capabilities.includes(item.capability),
  );

  return (
    <div className="flex h-screen bg-[var(--surface-primary)]">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:relative inset-y-0 left-0 z-50
          w-64 flex-shrink-0 flex flex-col
          bg-[var(--admin-sidebar-bg)] text-[var(--admin-sidebar-text)] border-r border-[var(--admin-sidebar-border)]
          transform transition-transform duration-300 ease-in-out
          lg:transform-none lg:transition-none
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Brand */}
        <div className="h-16 flex items-center gap-3 px-4 border-b border-[var(--admin-sidebar-border)]">
          <MatcoraBrandMark className="w-10 h-10 rounded-xl shadow-sm ring-1 ring-white/12" />
          <div>
            <span className="block font-semibold text-[var(--admin-sidebar-text)] leading-tight">Matcora Admin</span>
            <span className="block text-xs text-[var(--admin-sidebar-text-muted)]">Workspace analytics</span>
          </div>
          {/* Mobile close */}
          <button
            type="button"
            onClick={closeSidebar}
            className="lg:hidden ml-auto p-1.5 rounded-md hover:bg-[var(--admin-sidebar-hover)]"
            aria-label="Close sidebar"
          >
            <CloseIcon className="w-4 h-4 text-[var(--admin-sidebar-text-muted)]" />
          </button>
        </div>

        {/* Workspace label */}
        {workspace && (
          <div className="px-4 py-4 border-b border-[var(--admin-sidebar-border)] bg-[rgba(255,255,255,0.02)]">
            <p className="text-[11px] font-semibold text-[var(--admin-sidebar-text-muted)] uppercase tracking-[0.18em]">
              Workspace
            </p>
            <button
              type="button"
              onClick={() => { clearWorkspace(); onNavigate('workspaces'); closeSidebar(); }}
              className="mt-2 text-sm font-medium text-[var(--admin-sidebar-text)] hover:text-white transition-colors truncate block w-full text-left"
              title="Switch workspace"
            >
              {workspace.display_name}
            </button>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {visibleItems.map((item) => {
            const isActive = activePage === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => { onNavigate(item.id); closeSidebar(); }}
                className={`
                  w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium
                  transition-all duration-150
                  ${isActive
                    ? 'bg-[var(--admin-sidebar-active)] text-white shadow-[inset_0_0_0_1px_var(--admin-sidebar-active-ring)]'
                    : 'text-[var(--admin-sidebar-text-muted)] hover:bg-[var(--admin-sidebar-hover)] hover:text-white'
                  }
                `}
              >
                {item.icon}
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* User footer */}
        <div className="border-t border-[var(--admin-sidebar-border)] p-3 bg-[rgba(255,255,255,0.02)]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[rgba(255,255,255,0.09)] flex items-center justify-center text-xs font-medium text-white">
              {(user?.email?.[0] ?? '?').toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--admin-sidebar-text)] truncate">
                {user?.displayName ?? user?.email}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                clearWorkspace();
                void logout();
                closeSidebar();
              }}
              className="p-1.5 rounded-md text-[var(--admin-sidebar-text-muted)] hover:bg-[var(--admin-sidebar-hover)] hover:text-white"
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOutIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile header */}
        <header className="lg:hidden h-16 flex items-center gap-3 px-4 border-b border-[var(--border-default)] bg-[var(--surface-secondary)] shadow-[0_1px_0_rgba(15,23,42,0.06)]">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="p-1.5 -ml-1 rounded-md hover:bg-[var(--interactive-hover)]"
            aria-label="Open navigation"
          >
            <MenuIcon className="w-5 h-5 text-[var(--text-secondary)]" />
          </button>
          <MatcoraBrandMark className="w-8 h-8 rounded-lg ring-1 ring-[var(--border-default)]" />
          <span className="font-semibold text-[var(--text-primary)]">
            {workspace?.display_name ?? 'Matcora Admin'}
          </span>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-[var(--surface-primary)]">
          {children}
        </main>
      </div>
    </div>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────

function BarChartIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20V10" /><path d="M18 20V4" /><path d="M6 20v-4" />
    </svg>
  );
}

function ActivityIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
}

function MessageIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  );
}

function MenuIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 12h18M3 6h18M3 18h18" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

function LogOutIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}
